<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="
      person ? `Capture ID Photo for ${person.firstName} ${person.lastName}` : 'Upload ID Document'
    "
    :modal="true"
    :closable="false"
    :style="{ width: '90vw', maxWidth: '600px' }"
    :breakpoints="{ '960px': '90vw' }"
  >
    <div class="grid">
      <div class="col-12 mb-4">
        <div class="p-field">
          <div class="flex align-items-center justify-content-between">
            <label for="idDocument" class="block mb-2">ID Document (Front)</label>
            <small v-if="requireBackPhoto" class="text-orange-600">Back photo required</small>
          </div>
          <FileUpload
            id="idDocument"
            mode="basic"
            accept="image/*,application/pdf"
            :maxFileSize="50000000"
            chooseLabel="Upload ID Document"
            class="w-full"
            @select="onFileSelect"
          />
          <small class="text-gray-500">Upload driver's license, passport, or other ID</small>
        </div>
      </div>

      <!-- Preview section -->
      <div
        v-if="idCapture.state.uploadedImagePreview || idCapture.state.uploadedImageUrl"
        class="col-12 mb-4"
      >
        <div class="text-center">
          <img
            v-if="idCapture.state.uploadedImagePreview || idCapture.state.uploadedImageUrl"
            :src="
              idCapture.state.uploadedImagePreview || idCapture.state.uploadedImageUrl || undefined
            "
            class="border-round shadow-2"
            style="max-height: 200px; max-width: 100%"
          />
          <div v-else class="flex align-items-center justify-content-center">
            <i class="pi pi-file-pdf text-red-500 text-xl mr-2"></i>
            <span>PDF Document</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Cropper Dialog -->
    <Dialog
      :visible="idCapture.state.showCropperDialog"
      header="Crop ID Document"
      :modal="true"
      :closable="false"
      :style="{ width: '90vw', maxWidth: '600px' }"
      :breakpoints="{ '960px': '90vw' }"
    >
      <div class="cropper-container">
        <Cropper
          v-if="idCapture.state.cropperImage"
          class="cropper"
          :src="idCapture.state.cropperImage"
          :stencil-props="{
            aspectRatio: 5 / 3,
          }"
          image-restriction="stencil"
          :canvas="{
            width: 600,
            height: 400,
          }"
          ref="cropperRef"
        />

        <div class="flex justify-content-center mt-3">
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-outlined mr-2"
            @click="idCapture.rotateImage(-90)"
            aria-label="Rotate Left"
          />
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-outlined"
            style="transform: scaleX(-1)"
            @click="idCapture.rotateImage(90)"
            aria-label="Rotate Right"
          />
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text mr-2"
          @click="idCapture.cancelCrop"
        />
        <Button label="Crop" icon="pi pi-check" @click="idCapture.cropImage" />
      </template>
    </Dialog>

    <template #footer>
      <div class="flex align-items-center gap-2 w-full justify-content-between">
        <div class="flex align-items-center gap-2">
          <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="cancel" />
        </div>
        <div class="flex align-items-center gap-2">
          <Button
            v-if="!backSelected && requireBackPhoto"
            label="Next: Back"
            icon="pi pi-arrow-right"
            @click="captureBack"
            :disabled="!frontReady"
          />
          <Button
            v-else
            label="Save"
            icon="pi pi-check"
            @click="save"
            :loading="idCapture.state.isUploading"
            :disabled="!frontReady || (requireBackPhoto && !backReady)"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { watch, ref, computed } from 'vue'
import { useIdCapture } from '@/composables/useIdCapture'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import FileUpload from 'primevue/fileupload'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useSettingsStore } from '@/stores/settings'
import type { Person } from '@/types/Person'
import type { IDDocument } from '@/types/IDDocument'

const props = defineProps<{
  visible: boolean
  person?: Person | null
  idDocument?: IDDocument | null
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  save: [frontImageUrl: string, backImageUrl?: string]
  cancel: []
}>()

const cropperRef = ref(null)
const idCapture = useIdCapture(cropperRef)
const settingsStore = useSettingsStore()
const requireBackPhoto = computed(() => settingsStore.settings.idDocuments.requireBackPhoto)

// simple state to track front/back readiness
const frontImageUrl = ref<string | null>(null)
const backImageUrl = ref<string | null>(null)
const backSelected = ref(false)
const frontReady = computed(
  () =>
    !!(frontImageUrl.value || idCapture.state.uploadedImagePreview || idCapture.state.currentFile),
)
const backReady = computed(
  () =>
    !!(
      backImageUrl.value ||
      (backSelected.value && (idCapture.state.uploadedImagePreview || idCapture.state.currentFile))
    ),
)

// Watch for visibility changes
watch(
  () => props.visible,
  (newValue) => {
    if (!newValue) {
      idCapture.reset()
    }
  },
)

// Handle file selection
function onFileSelect(event: { files: File[] }) {
  const file = event.files[0]
  if (file) {
    idCapture.handleFileSelect(file)
  }
}

// Save the ID document
async function save() {
  try {
    // Upload front if needed
    if (!frontImageUrl.value) {
      if (!idCapture.state.currentFile) return
      const path = `person_documents/id_capture/front_${Date.now()}`
      frontImageUrl.value = await idCapture.uploadImage(path)
    }

    // Upload back if required and not yet uploaded
    if (requireBackPhoto.value && !backImageUrl.value) {
      if (!idCapture.state.currentFile) return
      const pathBack = `person_documents/id_capture/back_${Date.now()}`
      backImageUrl.value = await idCapture.uploadImage(pathBack)
    }

    // If we have an existing ID document, update it instead of creating new one
    if (props.idDocument?.id) {
      // Import the ID document store
      const { useIDDocumentStore } = await import('@/stores/idDocumentStore')
      const idDocumentStore = useIDDocumentStore()

      await idDocumentStore.update(props.idDocument.id, {
        frontImageUrl: frontImageUrl.value!,
        backImageUrl: backImageUrl.value || undefined,
      })
    }

    emit('save', frontImageUrl.value!, backImageUrl.value || undefined)
    emit('update:visible', false)
    resetAll()
  } catch (error) {
    console.error('Error saving ID document:', error)
  }
}

async function captureBack() {
  try {
    if (!idCapture.state.currentFile) return
    // First, upload front (if not yet)
    if (!frontImageUrl.value) {
      const pathFront = `person_documents/id_capture/front_${Date.now()}`
      frontImageUrl.value = await idCapture.uploadImage(pathFront)
    }
    // Reset capture state for back
    idCapture.reset()
    backSelected.value = true
  } catch (e) {
    console.error('Error preparing back capture:', e)
  }
}

// Cancel the dialog
function cancel() {
  emit('cancel')
  emit('update:visible', false)
  resetAll()
}

function resetAll() {
  frontImageUrl.value = null
  backImageUrl.value = null
  backSelected.value = false
  idCapture.reset()
}
</script>

<style scoped>
.cropper-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0;
}

.cropper {
  height: 400px;
  width: 100%;
  background: #f8f8f8;
}
</style>
